// @givmo/passkeys — the registration transport and the passkey management calls (AS-WEB11), over
// the generated operations for `/users/me/passkeys*` (all in the committed snapshot).
//
// ⚠ A REFUSAL IS RETHROWN AS THE API-CLIENT RAISED IT. Rule 1 (`step_up_required` /
// `passkey_confirmation_required`, action `add_passkey`) and rule 2 (`passkey_confirmation_required`,
// action `delete_passkey`) are answered by `@givmo/step-up`'s gate, which reads their machine code
// off the `GivmoError`; wrapping it here would hide the refusal from the gate and turn a
// "Confirm it's you" into a dead end.

import { sdk } from '@givmo/api-client';
import type { PasskeyRegistrationTransport } from '@givmo/auth';
import { extractPasskeyOptions } from './passkey-transports';

/** A 200 whose body carries no usable options: not a usable route. */
export class PasskeyOptionsUnusableError extends Error {
  constructor() {
    super('The passkey options could not be read.');
    this.name = 'PasskeyOptionsUnusableError';
    Object.setPrototypeOf(this, PasskeyOptionsUnusableError.prototype);
  }
}

export function createPasskeyRegistrationTransport(): PasskeyRegistrationTransport {
  return {
    async requestRegistrationOptions(init) {
      const { data } = await sdk.passkeysStartPasskeyRegistration({
        signal: init?.signal,
        throwOnError: true,
      });
      const options = extractPasskeyOptions(data);
      if (options === null) throw new PasskeyOptionsUnusableError();
      return options;
    },

    async finishRegistration(attestation, init) {
      await sdk.passkeysFinishPasskeyRegistration({
        // Stored unnamed: the person names a passkey, never the browser's guess (see `registerPasskey`).
        body: { credential: attestation, name: null },
        signal: init?.signal,
        throwOnError: true,
      });
    },
  };
}

/** Rename one of the account's passkeys; an empty name clears it (`null`), as the backend reads it. */
export async function renamePasskey(id: string, name: string): Promise<void> {
  const trimmed = name.trim();
  await sdk.passkeysRenameMyPasskey({
    path: { passkey_id: id },
    body: { name: trimmed.length > 0 ? trimmed : null },
    throwOnError: true,
  });
}

/** Remove one of the account's passkeys. A factor-rule refusal is rethrown for the gate. */
export async function removePasskey(id: string): Promise<void> {
  await sdk.passkeysDeleteMyPasskey({ path: { passkey_id: id }, throwOnError: true });
}
